import { isPlatformServer } from '@angular/common';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';

import { EnvironmentService } from 'src/services/environment/environment.service';
import { addSlashToEndOfUrl } from 'src/shared/helpers/add-slash-to-end-of-url/add-slash-to-end-of-url';

@Injectable({ providedIn: 'root' })
export class DeployUrlService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly environmentService = inject(EnvironmentService);

    private readonly DEFAULT_DEPLOY_URL = '/tp/static/';

    public get deployUrl(): string {
        return addSlashToEndOfUrl(this.environmentService.environment.deployUrl || this.DEFAULT_DEPLOY_URL);
    }

    public init(): void {
        if (isPlatformServer(this.platformId)) {
            this.prependToInitialServerResponseAssets(this.deployUrl);
            this.prependToLazyLoadedAssets(this.deployUrl);
        }
    }

    private prependToInitialServerResponseAssets(deployUrl: string): void {
        const links = this.document.querySelectorAll(`link[href^="styles."], link[rel="icon"][href]`);
        const scripts = this.document.querySelectorAll(`script[src^="main"], script[src^="runtime"]`);
        const preloadedChunks = this.document.querySelectorAll(`link[href^="chunk-"]`);

        if (scripts.length === 0) {
            console.error('DeployUrlService: Critical scripts (main/runtime) not found in the document');
            throw new Error('Required scripts (main/runtime) are missing from the document');
        }

        this.prependDeployUrl(links, 'href', deployUrl);
        this.prependDeployUrl(scripts, 'src', deployUrl);
        this.prependDeployUrl(preloadedChunks, 'href', deployUrl);
    }

    // Will be used as __webpack_public_path__ at browser runtime
    private prependToLazyLoadedAssets(deployUrl: string): void {
        const head = this.document.getElementsByTagName('head')[0];
        const script = this.document.createElement('script');

        script.type = 'text/javascript';
        script.text = `window.DEPLOY_URL = "${deployUrl}"`;
        head.insertBefore(script, head.firstChild);
    }

    private prependDeployUrl(elements: NodeListOf<Element>, urlAttribute: string, deployUrl: string): void {
        Array.prototype.forEach.call(elements, (element: Element) => {
            const initialUrl = element.getAttribute(urlAttribute);

            if (initialUrl.indexOf('http') !== 0 && initialUrl.indexOf(deployUrl) !== 0) {
                const finalUrl = deployUrl + initialUrl;
                element.setAttribute(urlAttribute, finalUrl);
            }
        });
    }
}
